import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAtom, useAtomValue } from "jotai";
import { Layout } from "react-grid-layout";
import { track, EVENTS } from "../../utils/analytics";

// Debounce helper for layout updates
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

import { DashboardConfiguration } from "../../types/DashboardConfiguration";
import { InsightDefinition, InsightTemplate } from "../../types/Insight";
import {
  getDashboardConfiguration,
  getDashboardInsights,
  updateLayout,
} from "../../api";
import {
  AccountAtom,
  DashboardLayoutStateStore,
  SelectedDashboardIdAtom,
} from "../../store";

const buildEmptyLayouts = () => ({
  lg: [],
  md: [],
  sm: [],
  xs: [],
  xxs: [],
});

export interface UseDashboardController {
  layouts: Record<string, Layout[]>;
  insightDefinitions: InsightDefinition[];
  updateLayoutComp: (updatedLayout: Layout[]) => void;
  setBreakpoint: (breakpoint: string) => void;
  loading: boolean;
  insightBeingResized: boolean;
  setInsightBeingResized: (value: boolean) => void;
  selectedDashboardId?: string;
}

export const useDashboardController = (): UseDashboardController => {
  const [dashboardConfiguration, setDashboardConfiguration] = useState<
    DashboardConfiguration | undefined
  >();

  const currentDashboardAndAccount = useRef({
    dashboardId: -1,
    accountId: -1,
  });

  const [insights, setInsights] = useState<InsightTemplate[]>([]);
  const [insightBeingResized, setInsightBeingResized] = useState(false);

  const [dashboardLayoutStore, setDashboardLayoutState] = useAtom(
    DashboardLayoutStateStore
  );

  const selectedDashboardId = useAtomValue(SelectedDashboardIdAtom);
  const accountState = useAtomValue(AccountAtom);

  const generateLayoutData = useCallback(
    (
      layout: Record<string, Layout[]>,
      datasets: InsightTemplate[]
    ): Record<string, Layout[]> => {
      return dashboardLayoutStore.breakpoints.reduce(
        (acc, breakpoint) => {
          acc[breakpoint] = (layout[breakpoint] || []).map((item: any) => ({
            ...item,
            data: datasets.find(
              (insight) => parseInt(insight.insightId) === parseInt(item.i)
            ),
          }));
          return acc;
        },
        {} as Record<string, Layout[]>
      );
    },
    [dashboardLayoutStore.breakpoints]
  );

  const loadDashboard = useCallback(async () => {
    handleResize();

    if (!selectedDashboardId || !accountState.selectedAccount?.id) return;

    try {
      const configResult = await getDashboardConfiguration(selectedDashboardId);
      if (configResult) {
        setDashboardConfiguration(configResult);

        const insightData = await getDashboardInsights(selectedDashboardId);
        if (insightData) {
          setInsights(insightData);
          const newLayoutData = generateLayoutData(
            configResult.layout,
            insightData ?? []
          );

          setDashboardLayoutState((prevState) => ({
            ...prevState,
            layouts: newLayoutData,
            gridLoading: false,
            enabledInsights: configResult.enabledInsights || [],
          }));
        }
      }
    } catch (error) {
      console.error("Error loading dashboard:", error);
    }
  }, [selectedDashboardId, accountState,]);

  useEffect(() => {
    if (selectedDashboardId && accountState.selectedAccount?.id) {
      // only call load once per dashboard/account pair
      if (
        currentDashboardAndAccount.current.dashboardId !==
          selectedDashboardId ||
        currentDashboardAndAccount.current.accountId !==
          accountState.selectedAccount?.id
      ) {
        currentDashboardAndAccount.current = {
          dashboardId: selectedDashboardId,
          accountId: accountState.selectedAccount?.id,
        };
        loadDashboard();

        // Track dashboard change
        track(EVENTS.DASHBOARD.DASHBOARD_CHANGED, {
          dashboardId: selectedDashboardId,
          accountId: accountState.selectedAccount?.id,
        });
      }
    }
  }, [selectedDashboardId, accountState.selectedAccount?.id]);

  const previousEnabledInsights = useRef(dashboardLayoutStore.enabledInsights);

  useEffect(() => {
    const currentEnabledInsights = dashboardLayoutStore.enabledInsights;

    if (
      currentEnabledInsights &&
      previousEnabledInsights.current &&
      JSON.stringify(currentEnabledInsights) !==
        JSON.stringify(previousEnabledInsights.current)
    ) {
      loadDashboard();
    }

    previousEnabledInsights.current = currentEnabledInsights;
  }, [dashboardLayoutStore.enabledInsights]);

  const setBreakpoint = useCallback(
    (breakpoint: string) => {
      if (breakpoint !== dashboardLayoutStore.currentBreakpoint) {
        setDashboardLayoutState((prevState) => ({
          ...prevState,
          currentBreakpoint: breakpoint,
        }));
      }
    },
    [dashboardLayoutStore.currentBreakpoint]
  );

  const handleResize = useCallback(() => {
    const width = window.innerWidth;
    if (width >= 1200) setBreakpoint("lg");
    else if (width >= 992) setBreakpoint("md");
    else if (width >= 768) setBreakpoint("sm");
    else if (width >= 576) setBreakpoint("xs");
    else if (width >= 250) setBreakpoint("xxs");
    else setBreakpoint("lg");
    window.dispatchEvent(new Event("resize"));
  }, [setBreakpoint]);

  // Separate function for immediate state update (for UI responsiveness)
  const updateLayoutState = useCallback((updatedLayout: Layout[]) => {
    const breakpoint = dashboardLayoutStore.currentBreakpoint;
    const currentLayoutData = dashboardLayoutStore.layouts[breakpoint];

    const updatedMergedLayout = updatedLayout.map((item: any) => ({
      ...item,
      data: currentLayoutData.find((i: any) => parseInt(i.i) === parseInt(item.i))
        ?.data,
    }));

    // Immediately update local state for UI responsiveness
    setDashboardLayoutState((prevState) => {
      // Only update if layout actually changed (prevent unnecessary re-renders)
      const currentLayout = prevState.layouts[breakpoint];
      const hasChanged = JSON.stringify(currentLayout) !== JSON.stringify(updatedMergedLayout);
      
      if (!hasChanged) return prevState;

      return {
        ...prevState,
        layouts: {
          ...prevState.layouts,
          [breakpoint]: updatedMergedLayout,
        },
      };
    });

    return updatedMergedLayout;
  }, [dashboardLayoutStore.currentBreakpoint, dashboardLayoutStore.layouts, setDashboardLayoutState]);

  // Debounced function for API persistence
  const persistLayoutToAPI = useCallback(
    debounce(async (updatedMergedLayout: Layout[], breakpoint: string) => {
      const layoutId =
        selectedDashboardId ??
        accountState.selectedAccount?.defaultDashboardConfigurationId;

      if (!layoutId) return;

      // Track layout update
      track(EVENTS.DASHBOARD.LAYOUT_UPDATED, {
        dashboardId: layoutId,
        breakpoint: breakpoint,
        insightCount: updatedMergedLayout.length,
      });

      try {
        const updatedConfig = await updateLayout(
          layoutId.toString(),
          {
            layout: {
              ...dashboardLayoutStore.layouts,
              [breakpoint]: updatedMergedLayout,
            },
          },
          breakpoint
        );

        // Update enabled insights from server response
        setDashboardLayoutState((prevState) => ({
          ...prevState,
          enabledInsights: updatedConfig.enabledInsights,
        }));
      } catch (error) {
        console.error("Error updating layout:", error);
      }
    }, 500), // 500ms debounce
    [
      dashboardLayoutStore.layouts,
      accountState,
      setDashboardLayoutState,
      selectedDashboardId,
    ]
  );

  const updateLayoutComp = useCallback(
    async (updatedLayout: Layout[]) => {
      const breakpoint = dashboardLayoutStore.currentBreakpoint;
      
      // Update local state immediately for responsiveness
      const updatedMergedLayout = updateLayoutState(updatedLayout);
      
      // Debounced API persistence
      persistLayoutToAPI(updatedMergedLayout, breakpoint);
    },
    [updateLayoutState, persistLayoutToAPI, dashboardLayoutStore.currentBreakpoint]
  );

  const insightDefinitions: InsightDefinition[] = useMemo(() => {
    const breakpoint = dashboardLayoutStore.currentBreakpoint ?? "md";
    const layoutItems = dashboardLayoutStore.layouts[breakpoint] || [];
    if (insights.length > 0 && dashboardConfiguration?.layout && layoutItems) {
      return layoutItems
        .map((item: any) => {
          const insight = insights.find(
            (x) => parseInt(x.insightId) === parseInt(item.i)
          );
          return insight ? { ...item, ...insight } : (null as any);
        })
        .filter(Boolean);
    }

    return [];
  }, [dashboardLayoutStore.currentBreakpoint, dashboardLayoutStore.layouts, insights, dashboardConfiguration]);

  return {
    layouts: {
      ...buildEmptyLayouts(),
      ...(dashboardLayoutStore.layouts || {}),
    },
    insightDefinitions,
    updateLayoutComp,
    setBreakpoint,
    loading: Boolean(
      selectedDashboardId &&
        accountState.selectedAccount?.id &&
        !dashboardConfiguration
    ),
    insightBeingResized,
    setInsightBeingResized,
    selectedDashboardId,
  };
};
